import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2,
  AlertTriangle,
  CreditCard,
  Mail,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { billingApi } from "@/services/api/billing";
import type { AdminBillingOrgRow } from "@/services/api/billing";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/portal/platform/billing-overview")({
  component: BillingOverviewPage,
});

type Mode = "off" | "invoice_only" | "auto_charge";

const MODE_LABEL: Record<string, string> = {
  off: "Off",
  invoice_only: "Invoice only",
  auto_charge: "Auto-charge",
};

function modeVariant(mode: string): "secondary" | "default" | "outline" {
  if (mode === "auto_charge") return "default";
  if (mode === "invoice_only") return "secondary";
  return "outline";
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "paid") return "default";
  if (status === "authorised") return "secondary";
  if (status === "draft") return "outline";
  return "destructive";
}

function needsAttention(org: AdminBillingOrgRow): boolean {
  return org.billing_email_missing || !org.card_on_file || org.suspended;
}

function lastMonths(count: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 1; i <= count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

function SummaryCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}

function OrgRow({
  org,
  selected,
  onToggle,
}: {
  org: AdminBillingOrgRow;
  selected: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <TableRow className={org.suspended ? "bg-destructive/5" : undefined}>
      <TableCell className="w-8">
        <Checkbox checked={selected} onCheckedChange={() => onToggle(org.id)} />
      </TableCell>
      <TableCell>
        <div className="font-medium">{org.name}</div>
        <div className="text-xs text-muted-foreground">{org.slug}</div>
      </TableCell>
      <TableCell>
        <Badge variant={modeVariant(org.billing_mode)}>
          {MODE_LABEL[org.billing_mode] ?? org.billing_mode}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">
        {org.billing_email ?? (
          <span className="inline-flex items-center gap-1 text-amber-600">
            <Mail className="h-3 w-3" /> falls back to owner
          </span>
        )}
      </TableCell>
      <TableCell className="text-sm">
        {org.vat_number ?? <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell>
        {org.card_on_file ? (
          <span className="inline-flex items-center gap-1 text-sm">
            <CreditCard className="h-3 w-3" /> Yes
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">No</span>
        )}
      </TableCell>
      <TableCell className="text-sm">
        {org.last_invoice ? (
          <div className="flex items-center gap-2">
            <Badge variant={statusVariant(org.last_invoice.status)}>
              {org.last_invoice.status}
            </Badge>
            <span>
              {org.last_invoice.currency}{" "}
              {org.last_invoice.amount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-sm">
        {org.suspended ? (
          <span className="inline-flex items-center gap-1 text-destructive">
            <AlertTriangle className="h-3 w-3" /> Suspended
          </span>
        ) : org.retry ? (
          <span className="text-amber-600">retry {org.retry.attempt}/3</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function BillingOverviewPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<string>("");
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState<string>("all");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);

  const months = useMemo(() => lastMonths(12), []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-billing-overview", period],
    queryFn: () => billingApi.getAdminOverview(token as string, period || undefined),
    enabled: !!token,
  });

  const filtered = useMemo(() => {
    const orgs = data?.organisations ?? [];
    const q = search.trim().toLowerCase();
    return orgs.filter((o) => {
      if (q && !o.name.toLowerCase().includes(q) && !o.slug.toLowerCase().includes(q)) {
        return false;
      }
      if (modeFilter !== "all" && o.billing_mode !== modeFilter) return false;
      if (attentionOnly && !needsAttention(o)) return false;
      return true;
    });
  }, [data, search, modeFilter, attentionOnly]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    const allSelected = filtered.length > 0 && filtered.every((o) => selected.has(o.id));
    setSelected(allSelected ? new Set() : new Set(filtered.map((o) => o.id)));
  }

  async function applyBulkMode(mode: Mode) {
    if (!token || selected.size === 0) return;
    setApplying(true);
    const ids = [...selected];
    let ok = 0;
    for (const id of ids) {
      try {
        await billingApi.setBillingMode(token, id, mode);
        ok++;
      } catch {
        // continue; report the tally at the end
      }
    }
    setApplying(false);
    setSelected(new Set());
    await queryClient.invalidateQueries({ queryKey: ["admin-billing-overview"] });
    if (ok === ids.length) {
      toast.success(`Set ${ok} org${ok === 1 ? "" : "s"} to ${MODE_LABEL[mode]}`);
    } else {
      toast.error(`Set ${ok}/${ids.length} — ${ids.length - ok} failed`);
    }
  }

  const allSelected = filtered.length > 0 && filtered.every((o) => selected.has(o.id));

  return (
    <div className="space-y-6 p-1">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Billing</h1>
          <p className="text-sm text-muted-foreground">
            Per-org billing state and the monthly paid-vs-unpaid view.
          </p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Last month" />
          </SelectTrigger>
          <SelectContent>
            {months.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : isError || !data ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-destructive">
            Failed to load billing overview.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
            <SummaryCard label="Organisations" value={data.summary.total_orgs} />
            <SummaryCard label="Auto-charge" value={data.summary.by_mode.auto_charge ?? 0} />
            <SummaryCard label="Invoice only" value={data.summary.by_mode.invoice_only ?? 0} />
            <SummaryCard label="Suspended" value={data.summary.suspended} />
            <SummaryCard label="No billing email" value={data.summary.missing_billing_email} />
            <SummaryCard label="No card" value={data.summary.no_card} />
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {data.summary.period} — invoiced &amp; collected
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-6 text-sm">
              <div>
                <div className="text-muted-foreground">Invoiced</div>
                <div className="font-medium">
                  {Object.keys(data.summary.invoiced_by_currency).length === 0
                    ? "—"
                    : Object.entries(data.summary.invoiced_by_currency)
                        .map(([cur, amt]) => `${cur} ${amt.toLocaleString()}`)
                        .join("  ·  ")}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Paid</div>
                <div className="font-medium text-green-600">{data.summary.paid_count}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Unpaid</div>
                <div className="font-medium text-amber-600">{data.summary.unpaid_count}</div>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or slug…"
              className="w-64"
            />
            <Select value={modeFilter} onValueChange={setModeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="off">Off</SelectItem>
                <SelectItem value="invoice_only">Invoice only</SelectItem>
                <SelectItem value="auto_charge">Auto-charge</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={attentionOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setAttentionOnly((v) => !v)}
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              Needs attention
            </Button>
            <span className="ml-auto text-sm text-muted-foreground">
              {filtered.length} of {data.organisations.length}
            </span>
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-3">
              <span className="text-sm font-medium">{selected.size} selected</span>
              <span className="text-sm text-muted-foreground">Set billing mode:</span>
              <Select
                onValueChange={(v) => applyBulkMode(v as Mode)}
                disabled={applying}
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder={applying ? "Applying…" : "Choose mode"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="invoice_only">Invoice only</SelectItem>
                  <SelectItem value="auto_charge">Auto-charge</SelectItem>
                </SelectContent>
              </Select>
              {applying && <Loader2 className="h-4 w-4 animate-spin" />}
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                <X className="mr-1 h-4 w-4" /> Clear
              </Button>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell className="w-8">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                    </TableCell>
                    <TableCell className="font-medium">Organisation</TableCell>
                    <TableCell className="font-medium">Mode</TableCell>
                    <TableCell className="font-medium">Billing email</TableCell>
                    <TableCell className="font-medium">VAT</TableCell>
                    <TableCell className="font-medium">Card</TableCell>
                    <TableCell className="font-medium">Last invoice</TableCell>
                    <TableCell className="font-medium">Status</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((org) => (
                    <OrgRow
                      key={org.id}
                      org={org}
                      selected={selected.has(org.id)}
                      onToggle={toggle}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
